import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { toAccountView } from '@/lib/adapters/account';
import UnavailablePanel from '@/components/UnavailablePanel';

interface TopNavProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  onSearchOpen?: () => void;
}

const navItems = [
  { id: 'overview', label: 'Dashboard' },
  { id: 'triage', label: 'Triage' },
  { id: 'client-portal', label: 'Clients' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'recurring-tasks', label: 'Templates' },
  { id: 'workflows', label: 'Workflows' },
  { id: 'staff-workload', label: 'Team' },
  { id: 'reports', label: 'Reports' },
];

export default function TopNav({ activeSection, setActiveSection, onSearchOpen }: TopNavProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const { isDark } = useTheme();
  const { user, logout, isDemoMode } = useAuth();
  const navigate = useNavigate();
  const account = useMemo(() => (user ? toAccountView(user) : null), [user]);

  const signOut = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // Demo mode has no session to end: /login redirects straight back, so offer no Sign Out there.
  const profileItems: { label: string; onClick: () => void }[] = [
    { label: 'Profile Settings', onClick: () => setActiveSection('settings') },
    { label: 'Billing', onClick: () => setActiveSection('settings') },
    { label: 'Help Center', onClick: () => window.open('https://docs.absee.biz', '_blank', 'noopener,noreferrer') },
    ...(isDemoMode ? [] : [{ label: 'Sign Out', onClick: signOut }]),
  ];

  const bg = isDark ? 'bg-[#0D0F14]/95 border-white/[0.12]' : 'bg-white/95 border-gray-200/80';
  const logoText = isDark ? 'text-white' : 'text-gray-900';
  const navActive = isDark
    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
    : 'bg-teal-50 text-teal-700 border border-teal-200';
  const navInactive = isDark
    ? 'text-gray-400 hover:text-gray-200 hover:bg-white/5'
    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100';
  const iconBtn = isDark
    ? 'text-gray-400 hover:text-gray-200 hover:bg-white/8'
    : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100';
  const dropdownBg = isDark ? 'bg-[#13161E] border-white/[0.08]' : 'bg-white border-gray-200 shadow-xl';
  const dropdownHeader = isDark ? 'border-white/[0.12]' : 'border-gray-100';
  const dropdownTitle = isDark ? 'text-white' : 'text-gray-900';
  const dropdownSub = isDark ? 'text-gray-400' : 'text-gray-500';
  const dropdownItem = isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50';

  return (
    <header className={`h-14 ${bg} backdrop-blur-md border-b flex items-center px-3 md:px-6 gap-3 md:gap-6 z-20 relative flex-shrink-0 overflow-hidden transition-colors duration-300`}>
      {/* Logo */}
      <div className="flex items-center gap-2.5 mr-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/30">
          <i className="ri-book-2-line text-white text-sm"></i>
        </div>
        <span className={`text-sm font-bold ${logoText} whitespace-nowrap tracking-wide`}>BookKeeper</span>
      </div>

      {/* Nav Items */}
      <nav className="hidden md:flex items-center gap-0.5">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveSection(item.id)}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeSection === item.id ? navActive : navInactive
            }`}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {/* Right side */}
      <div className="ml-auto flex items-center gap-3">
        {/* Search */}
        <button
          data-tour="search"
          onClick={onSearchOpen}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all cursor-pointer ${isDark ? 'bg-white/5 hover:bg-white/10 text-gray-400 border border-white/[0.12]' : 'bg-gray-100 hover:bg-gray-200 text-gray-500 border border-gray-200'}`}
        >
          <i className="ri-search-line text-sm"></i>
          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} whitespace-nowrap`}>Search...</span>
          <kbd className={`text-[10px] px-1.5 py-0.5 rounded border ml-1 whitespace-nowrap ${isDark ? 'bg-white/8 text-gray-500 border-white/10' : 'bg-white text-gray-500 border-gray-200'}`}>⌘K</kbd>
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => { setShowNotifications(!showNotifications); setShowProfile(false); }}
            aria-label="Notifications"
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer relative ${iconBtn}`}
          >
            <i className="ri-notification-3-line text-base"></i>
          </button>
          {showNotifications && (
            <div className={`absolute right-0 top-11 w-80 ${dropdownBg} rounded-xl shadow-2xl border z-50 overflow-hidden`}>
              <div className="p-3">
                <UnavailablePanel title="Notifications" reason="Notifications aren't available yet." />
              </div>
            </div>
          )}
        </div>

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => { setShowProfile(!showProfile); setShowNotifications(false); }}
            aria-label="User profile menu"
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-xs font-bold shadow-md shadow-teal-500/30">
              {account?.initials ?? '—'}
            </div>
            <i className={`ri-arrow-down-s-line text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}></i>
          </button>
          {showProfile && (
            <div className={`absolute right-0 top-11 w-52 ${dropdownBg} rounded-xl shadow-2xl border z-50 overflow-hidden`}>
              <div className={`px-4 py-3 border-b ${dropdownHeader}`}>
                <p className={`text-sm font-semibold ${dropdownTitle}`}>{account?.name ?? '—'}</p>
                <p className={`text-xs ${dropdownSub}`}>{account?.email ?? '—'}</p>
              </div>
              {profileItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setShowProfile(false);
                    item.onClick();
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm cursor-pointer whitespace-nowrap transition-colors ${dropdownItem}`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}